import React, { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Key, Plus, Server, Eye, EyeOff } from "lucide-react";
import {
  ConnectorCredentialsModal,
  Connector,
  Credentials,
  safeBase64Encode,
  safeBase64Decode,
} from "../../shared/ConnectorCredentialsForm";
import { CreateSftpAccountDto, SftpAccountDto } from "../../../types/sftp";

interface SftpCredentials {
  username: string;
  password: string;
  url: string;
  port: number;
}

interface ConnectorCredential {
  id: string;
  hotel_id: string;
  connector_type: string;
  connector_code: string;
  connector_hotel_id: string;
  base_url: string;
  reqParams: string;
  created_at: string;
  updated_at: string;
  is_primary: boolean;
  // Decoded fields for display
  decodedType?: string;
  decodedName?: string;
  decodedBaseUrl?: string;
  decodedHotelId?: string;
  // Actual connector name from connectors table
  connectorName?: string;
  // Flag to indicate if this is a cloudified connector
  cloudified?: boolean;
}

export function PMSCredentials() {
  const [connectorCredentials, setConnectorCredentials] = useState<
    ConnectorCredential[]
  >([]);
  const [availableConnectors, setAvailableConnectors] = useState<Connector[]>(
    []
  );
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(
    null
  );
  const [credentials, setCredentials] = useState<Credentials>({});
  const [showNewCredentialModal, setShowNewCredentialModal] = useState(false);
  const [showUpdateCredentialModal, setShowUpdateCredentialModal] =
    useState(false);
  const [currentCredentialId, setCurrentCredentialId] = useState<string | null>(
    null
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [sftpCredentials, setSftpCredentials] =
    useState<SftpCredentials | null>(null);
  const [primaryConnectorName, setPrimaryConnectorName] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    loadConnectorCredentials();
  }, []);

  const loadConnectorCredentials = async () => {
    try {
      setLoading(true);
      // Reset SFTP credentials
      setSftpCredentials(null);
      setPrimaryConnectorName("");

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setError("User not authenticated");
        return;
      }

      // Load all connector credentials for this hotel
      const { data: credentialsData, error: credentialsError } = await supabase
        .from("credentials")
        .select("*")
        .eq("hotel_id", user.id);

      if (credentialsError) throw credentialsError;

      // Get all connector codes from credentials
      const connectorCodes =
        credentialsData?.map((cred) => cred.connector_code) || [];

      // Fetch connector details from connectors table if we have any credentials
      let connectorDetailsMap: Record<string, any> = {};

      if (connectorCodes.length > 0) {
        const { data: connectorsData, error: connectorsError } = await supabase
          .from("connectors")
          .select("code, name, cloudified")
          .in(
            "code",
            connectorCodes.map((code) => code.toLowerCase())
          );

        if (connectorsError) throw connectorsError;

        // Create a map of connector code to details for quick lookup
        connectorDetailsMap = (connectorsData || []).reduce(
          (map, connector) => {
            map[connector.code.toLowerCase()] = {
              name: connector.name,
              cloudified: connector.cloudified,
            };
            return map;
          },
          {} as Record<string, any>
        );
      }

      // Process credentials for display
      const processedCredentials =
        credentialsData?.map((cred) => {
          try {
            // Try to decode the reqParams to extract display information
            const decodedData = JSON.parse(atob(cred.reqParams));

            // Get connector details from the map
            const connectorDetails =
              connectorDetailsMap[cred.connector_code.toLowerCase()];

            const connectorName = connectorDetails?.name || null;
            const isCloudified = connectorDetails?.cloudified || false;

            return {
              ...cred,
              // Ensure is_primary is a boolean
              is_primary: cred.is_primary === true,
              decodedType: cred.connector_type || decodedData.pms,
              decodedName: decodedData.pms
                ? decodedData.pms.charAt(0).toUpperCase() +
                  decodedData.pms.slice(1)
                : cred.connector_code,
              // Use the direct fields from the credentials table
              decodedBaseUrl: cred.base_url || "N/A",
              decodedHotelId: cred.connector_hotel_id || "N/A",
              // Add the connector name from connectors table
              connectorName: connectorName || null,
              // Add cloudified flag
              cloudified: isCloudified,
            };
          } catch (e) {
            console.error("Error decoding credentials:", e);
            // Get connector details from the map
            const connectorDetails =
              connectorDetailsMap[cred.connector_code.toLowerCase()];

            const connectorName = connectorDetails?.name || null;
            const isCloudified = connectorDetails?.cloudified || false;

            return {
              ...cred,
              // Ensure is_primary is a boolean
              is_primary: cred.is_primary === true,
              decodedType: cred.connector_type,
              decodedName: cred.connector_code,
              decodedBaseUrl: cred.base_url || "N/A",
              decodedHotelId: cred.connector_hotel_id || "N/A",
              // Add the connector name from connectors table
              connectorName: connectorName || null,
              // Add cloudified flag
              cloudified: isCloudified,
            };
          }
        }) || [];

      setConnectorCredentials(processedCredentials);

      // Find the primary connector and check if it's cloudified
      const primaryConnector = processedCredentials.find(
        (cred) =>
          cred.is_primary && cred.connector_type === "pms" && cred.cloudified
      );

      // If we have a primary cloudified connector, try to load SFTP credentials
      if (primaryConnector) {
        try {
          const decodedParams = safeBase64Decode(primaryConnector.reqParams);
          if (decodedParams && decodedParams.sftp) {
            setSftpCredentials(decodedParams.sftp);
            setPrimaryConnectorName(
              primaryConnector.connectorName || primaryConnector.connector_code
            );
          }
        } catch (decodeError) {
          console.error("Error decoding SFTP credentials:", decodeError);
        }
      }
    } catch (err) {
      console.error("Error loading connector credentials:", err);
      setError("Failed to load connector credentials");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableConnectors = async () => {
    try {
      setLoading(true);

      // Get all active connectors
      const { data: allConnectors, error: connectorsError } = await supabase
        .from("connectors")
        .select("id, code, name, type, status, credentials, cloudified")
        .eq("type", "pms")
        .eq("status", "active")
        .order("name");

      if (connectorsError) throw connectorsError;

      // Filter out connectors that are already connected
      const connectedCodes = connectorCredentials.map((cred) =>
        cred.connector_code.toLowerCase()
      );
      const filteredConnectors =
        allConnectors?.filter(
          (connector) => !connectedCodes.includes(connector.name.toLowerCase())
        ) || [];

      setAvailableConnectors(filteredConnectors);
    } catch (err) {
      console.error("Error loading available connectors:", err);
      setError("Failed to load available connectors");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectorSelect = (connector: Connector) => {
    setSelectedConnector(connector);
    // Initialize credentials object with empty values for each required field
    const initialCredentials: Credentials = {
      // Preserve the is_primary value
      is_primary: credentials.is_primary || false,
    };

    // Only process credentials if they exist (might be null for cloudified connectors)
    if (connector.credentials && connector.credentials.length > 0) {
      connector.credentials.forEach((field) => {
        initialCredentials[field.key] = "";
      });
    }

    setCredentials(initialCredentials);
  };

  const handleCredentialChange = (key: string, value: string | boolean) => {
    setCredentials((prev) => {
      const updated = {
        ...prev,
        [key]: value,
      };
      return updated;
    });
  };

  const handleNewCredential = () => {
    setSelectedConnector(null);
    setCredentials({ is_primary: false });
    setError("");
    loadAvailableConnectors();
    setShowNewCredentialModal(true);
  };

  const handleUpdateCredential = async (credentialId: string) => {
    // Reset state
    setSelectedConnector(null);
    setCredentials({});
    setError("");

    // Find the credential before showing the modal
    const credential = connectorCredentials.find((c) => c.id === credentialId);
    if (credential) {
      try {
        // Wait for the connector to load before showing the modal
        // Pass the credential ID directly to avoid race conditions with state updates
        await loadConnectorForUpdate(credential.connector_code, credentialId);

        // Set the ID after loading the connector data
        setCurrentCredentialId(credentialId);

        // Now show the modal after credentials are loaded
        setShowUpdateCredentialModal(true);
      } catch (err) {
        console.error("Error preparing update modal:", err);
        setError("Failed to load connector details");
      }
    } else {
      setError("Credential not found");
    }
  };

  const loadConnectorForUpdate = async (
    connectorCode: string,
    credentialId?: string
  ): Promise<void> => {
    try {
      const { data: connectorData, error: connectorError } = await supabase
        .from("connectors")
        .select("id, code, name, type, status, credentials, cloudified")
        .eq("type", "pms")
        .ilike("code", connectorCode.toLowerCase())
        .maybeSingle();

      if (connectorError) throw connectorError;

      if (!connectorData) {
        throw new Error(`Connector not found for code: ${connectorCode}`);
      }

      setSelectedConnector(connectorData);

      // Get the current credential to load existing values
      // Use the passed credentialId if available, otherwise fall back to the state variable
      const credId = credentialId || currentCredentialId;
      const currentCredential = connectorCredentials.find(
        (c) => c.id === credId
      );

      if (!currentCredential) {
        throw new Error(`Current credential not found for ID: ${credId}`);
      }

      // Initialize credentials with existing values
      const initialCredentials: Credentials = {};

      // Add the connector_hotel_id and is_primary fields (always required)
      initialCredentials["connector_hotel_id"] =
        currentCredential.connector_hotel_id || "";
      initialCredentials["is_primary"] = currentCredential.is_primary || false;

      // Only add base_url for non-cloudified connectors
      if (!connectorData.cloudified) {
        initialCredentials["base_url"] = currentCredential.base_url || "";
      }

      // Skip credential fields for cloudified connectors
      if (!connectorData.cloudified) {
        // Try to decode the stored credentials to get existing values
        if (currentCredential.reqParams) {
          try {
            const decodedParams = JSON.parse(atob(currentCredential.reqParams));

            // If we have credentials in the decoded data, use them
            if (decodedParams && decodedParams.credentials) {
              // Add the connector-specific credential fields with existing values if available
              if (
                connectorData.credentials &&
                connectorData.credentials.length > 0
              ) {
                connectorData.credentials.forEach((field: any) => {
                  initialCredentials[field.key] =
                    decodedParams.credentials[field.key] || "";
                });
              }
            } else {
              // Fallback if credentials structure is different
              if (
                connectorData.credentials &&
                connectorData.credentials.length > 0
              ) {
                connectorData.credentials.forEach((field: any) => {
                  initialCredentials[field.key] =
                    (decodedParams && decodedParams[field.key]) || "";
                });
              }
            }
          } catch (e) {
            console.error("Error decoding stored credentials:", e);
            // If decoding fails, initialize with empty values
            if (
              connectorData.credentials &&
              connectorData.credentials.length > 0
            ) {
              connectorData.credentials.forEach((field: any) => {
                initialCredentials[field.key] = "";
              });
            }
          }
        } else {
          // If no reqParams, initialize with empty values
          if (
            connectorData.credentials &&
            connectorData.credentials.length > 0
          ) {
            connectorData.credentials.forEach((field: any) => {
              initialCredentials[field.key] = "";
            });
          }
        }
      } else {
        // For cloudified connectors, check if SFTP account exists
        if (currentCredential.reqParams) {
          try {
            const decodedParams = JSON.parse(atob(currentCredential.reqParams));
            if (decodedParams && decodedParams.sftp) {
              console.log("SFTP account exists for this cloudified connector");
            }
          } catch (e) {
            console.error("Error checking SFTP account:", e);
          }
        }
      }

      setCredentials(initialCredentials);

      // Return successfully
      return Promise.resolve();
    } catch (err) {
      console.error("Error loading connector for update:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Failed to load connector details: ${errorMessage}`);

      // Reset state on error
      setSelectedConnector(null);
      setCredentials({});

      return Promise.reject(
        new Error(`Failed to load connector: ${errorMessage}`)
      );
    }
  };

  const handleSubmitNewCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      if (!selectedConnector) {
        throw new Error("Please select a connector");
      }

      // Always validate connector_hotel_id
      if (!credentials.connector_hotel_id) {
        throw new Error("Hotel ID is required");
      }

      // Only validate base_url for non-cloudified connectors
      if (!selectedConnector.cloudified) {
        // Validate base_url
        if (!credentials.base_url) {
          throw new Error("Connector URL is required");
        }

        // Validate URL format
        try {
          // Ensure base_url is a string before passing to URL constructor
          if (typeof credentials.base_url !== "string") {
            throw new Error("Connector URL must be a string");
          }
          new URL(credentials.base_url);
        } catch (e) {
          throw new Error("Please enter a valid Connector URL");
        }
      }

      // Validate all required credentials are provided (if any)
      if (
        selectedConnector.credentials &&
        selectedConnector.credentials.length > 0
      ) {
        const missingFields = selectedConnector.credentials
          .filter((field) => !credentials[field.key])
          .map((field) => field.key);

        if (missingFields.length > 0) {
          throw new Error(
            `Missing required fields: ${missingFields.join(", ")}`
          );
        }
      }

      // Get hotel details to retrieve hotel_code
      const { data: hotelData, error: hotelError } = await supabase
        .from("hotel_details")
        .select("hotel_code")
        .eq("id", user.id)
        .single();

      if (hotelError) throw hotelError;
      if (!hotelData) throw new Error("Hotel details not found");

      // Create connector_credentials JSON object with the required structure
      // Define the type explicitly to include optional sftp property
      let connector_credentials: {
        credentials?: { [key: string]: string | boolean };
        sftp?: {
          username: string;
          password: string;
          url: string;
          port: number;
        };
      };

      // Check if the connector is cloudified and create SFTP account if needed
      if (selectedConnector.cloudified === true) {
        // For cloudified connectors, we only need the SFTP details
        connector_credentials = {};
        // Create SFTP account
        const createSftpDto: CreateSftpAccountDto = {
          hotelId: hotelData.hotel_code.toLowerCase(),
        };

        // Get the API base URL from environment variables
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";

        // Call the API to create SFTP account
        const response = await fetch(`${apiBaseUrl}/v1/sftp/accounts`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(createSftpDto),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create SFTP account");
        }

        // Parse the response
        const sftpAccount: SftpAccountDto = await response.json();

        // Add SFTP account details to connector_credentials
        connector_credentials.sftp = {
          username: sftpAccount.username,
          password: sftpAccount.password,
          url: sftpAccount.url,
          port: sftpAccount.port,
        };
      } else {
        // For non-cloudified connectors, add the credentials
        connector_credentials = {
          credentials: { ...credentials },
        };

        // Remove fields that should not be in the encoded credentials
        if (connector_credentials.credentials && credentials.base_url) {
          delete connector_credentials.credentials.base_url;
        }
        if (
          connector_credentials.credentials &&
          credentials.connector_hotel_id
        ) {
          delete connector_credentials.credentials.connector_hotel_id;
        }
      }

      // Base64 encode the connector_credentials
      const encodedCredentials = safeBase64Encode(connector_credentials);

      // Check if this is set as primary
      const isPrimary = credentials.is_primary === true;

      if (isPrimary) {
        // Find any existing primary credentials with the same connector_type for this hotel
        const { data: existingPrimary, error: primaryCheckError } =
          await supabase
            .from("credentials")
            .select("id, connector_code")
            .eq("hotel_id", user.id)
            .eq("connector_type", "pms") // Check at connector_type level
            .eq("is_primary", true);

        if (primaryCheckError) throw primaryCheckError;

        // If there are existing primary credentials, update them to not be primary
        if (existingPrimary && existingPrimary.length > 0) {
          const { error: updatePrimaryError } = await supabase
            .from("credentials")
            .update({ is_primary: false })
            .eq("hotel_id", user.id)
            .eq("connector_type", "pms") // Update at connector_type level
            .eq("is_primary", true);

          if (updatePrimaryError) throw updatePrimaryError;
        }
      }

      // Create PMS credentials with the new format
      const { error: credentialsError } = await supabase
        .from("credentials")
        .insert([
          {
            hotel_id: user.id,
            connector_type: "pms",
            connector_code: selectedConnector.name.toLowerCase(),
            connector_hotel_id:
              credentials.connector_hotel_id || hotelData.hotel_code,
            base_url: credentials.base_url || "",
            reqParams: encodedCredentials,
            is_primary: isPrimary,
          },
        ]);

      if (credentialsError) throw credentialsError;

      // Close modal and refresh data
      setShowNewCredentialModal(false);
      await loadConnectorCredentials();
    } catch (err: any) {
      console.error("Connector setup error:", err);
      setError(err.message || "Failed to save connector credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUpdateCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      if (!selectedConnector) {
        throw new Error("Connector details not found");
      }

      if (!currentCredentialId) {
        throw new Error("Credential ID not found");
      }

      // Always validate connector_hotel_id
      if (!credentials.connector_hotel_id) {
        throw new Error("Hotel ID is required");
      }

      // Only validate base_url for non-cloudified connectors
      if (!selectedConnector.cloudified) {
        // Validate base_url
        if (!credentials.base_url) {
          throw new Error("Connector URL is required");
        }

        // Validate URL format
        try {
          // Ensure base_url is a string before passing to URL constructor
          if (typeof credentials.base_url !== "string") {
            throw new Error("Connector URL must be a string");
          }
          new URL(credentials.base_url);
        } catch (e) {
          throw new Error("Please enter a valid Connector URL");
        }
      }

      // Validate all required credentials are provided (if any)
      if (
        selectedConnector.credentials &&
        selectedConnector.credentials.length > 0
      ) {
        const missingFields = selectedConnector.credentials
          .filter((field) => !credentials[field.key])
          .map((field) => field.key);

        if (missingFields.length > 0) {
          throw new Error(
            `Missing required fields: ${missingFields.join(", ")}`
          );
        }
      }

      // Get the current credential to preserve connector_hotel_id
      const currentCredential = connectorCredentials.find(
        (c) => c.id === currentCredentialId
      );
      if (!currentCredential) {
        throw new Error("Current credential not found");
      }

      // Get hotel details to retrieve hotel_code for SFTP account creation if needed
      const { data: hotelData, error: hotelError } = await supabase
        .from("hotel_details")
        .select("hotel_code")
        .eq("id", user.id)
        .single();

      if (hotelError) throw hotelError;
      if (!hotelData) throw new Error("Hotel details not found");

      // Create connector_credentials JSON object with the required structure
      // Define the type explicitly to include optional sftp property
      let connector_credentials: {
        credentials?: { [key: string]: string | boolean };
        sftp?: {
          username: string;
          password: string;
          url: string;
          port: number;
        };
      };

      // For cloudified connectors, we only need the SFTP details
      if (selectedConnector.cloudified === true) {
        connector_credentials = {};
      } else {
        // For non-cloudified connectors, add the credentials
        connector_credentials = {
          credentials: { ...credentials },
        };

        // Remove fields that should not be in the encoded credentials
        if (connector_credentials.credentials && credentials.base_url) {
          delete connector_credentials.credentials.base_url;
        }
        if (
          connector_credentials.credentials &&
          credentials.connector_hotel_id
        ) {
          delete connector_credentials.credentials.connector_hotel_id;
        }
      }

      // Check if the connector is cloudified and create SFTP account if needed
      if (selectedConnector.cloudified === true) {
        // Try to decode existing credentials to check if SFTP account already exists
        let hasSftpAccount = false;
        if (currentCredential.reqParams) {
          try {
            const decodedParams = JSON.parse(atob(currentCredential.reqParams));
            hasSftpAccount = decodedParams && decodedParams.sftp;
          } catch (e) {
            console.error("Error decoding stored credentials:", e);
          }
        }

        // Only create a new SFTP account if one doesn't already exist
        if (!hasSftpAccount) {
          // Create SFTP account
          const createSftpDto: CreateSftpAccountDto = {
            hotelId: hotelData.hotel_code.toLowerCase(),
          };

          // Get the API base URL from environment variables
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";

          // Call the API to create SFTP account
          const response = await fetch(`${apiBaseUrl}/v1/sftp/accounts`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(createSftpDto),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              errorData.message || "Failed to create SFTP account"
            );
          }

          // Parse the response
          const sftpAccount: SftpAccountDto = await response.json();

          // Add SFTP account details to connector_credentials
          connector_credentials.sftp = {
            username: sftpAccount.username,
            password: sftpAccount.password,
            url: sftpAccount.url,
            port: sftpAccount.port,
          };
        } else {
          // Preserve existing SFTP account details
          try {
            const decodedParams = JSON.parse(atob(currentCredential.reqParams));
            if (decodedParams && decodedParams.sftp) {
              connector_credentials.sftp = decodedParams.sftp;
            }
          } catch (e) {
            console.error("Error preserving SFTP account details:", e);
          }
        }
      }

      // Base64 encode the connector_credentials
      const encodedCredentials = safeBase64Encode(connector_credentials);

      // Check if this is set as primary and the same connector_type already has a primary
      const isPrimary = credentials.is_primary === true;

      if (isPrimary) {
        // Find any existing primary credentials with the same connector_type (excluding current one)
        const { data: existingPrimary, error: primaryCheckError } =
          await supabase
            .from("credentials")
            .select("id")
            .eq("hotel_id", user.id)
            .eq("connector_type", "pms") // Check at connector_type level
            .eq("is_primary", true)
            .neq("id", currentCredentialId);

        if (primaryCheckError) throw primaryCheckError;

        // If there are existing primary credentials, update them to not be primary
        if (existingPrimary && existingPrimary.length > 0) {
          const { error: updatePrimaryError } = await supabase
            .from("credentials")
            .update({ is_primary: false })
            .eq("hotel_id", user.id)
            .eq("connector_type", "pms") // Update at connector_type level
            .eq("is_primary", true)
            .neq("id", currentCredentialId);

          if (updatePrimaryError) throw updatePrimaryError;
        }
      }

      // Update the credentials
      const { error: updateError } = await supabase
        .from("credentials")
        .update({
          reqParams: encodedCredentials,
          base_url: credentials.base_url || currentCredential.base_url || "",
          connector_hotel_id:
            credentials.connector_hotel_id ||
            currentCredential.connector_hotel_id,
          is_primary: isPrimary,
          updated_at: new Date().toISOString(),
        })
        .eq("id", currentCredentialId);

      if (updateError) throw updateError;

      // Close modal and refresh data
      setShowUpdateCredentialModal(false);
      setCurrentCredentialId(null);
      setSelectedConnector(null);
      setCredentials({});
      await loadConnectorCredentials();
    } catch (err: any) {
      console.error("Update credential error:", err);
      setError(err.message || "Failed to update credentials");
    } finally {
      setLoading(false);
    }
  };

  if (loading && connectorCredentials.length === 0) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="px-6 py-4 text-center text-sm text-gray-500">
            Loading connector credentials...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Key className="h-6 w-6 text-indigo-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">
              Connector Credentials
            </h2>
          </div>
          <button
            onClick={handleNewCredential}
            className="btn-primary flex items-center"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Credentials
          </button>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Table of connector credentials */}
        {connectorCredentials.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Base URL
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Hotel ID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Primary
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {connectorCredentials.map((credential) => (
                  <tr key={credential.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(
                        credential.decodedType || credential.connector_type
                      ).toUpperCase()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {credential.connectorName ||
                        credential.decodedName ||
                        credential.connector_code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {credential.decodedBaseUrl || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {credential.decodedHotelId || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                      {credential.is_primary ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Primary
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          Secondary
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleUpdateCredential(credential.id)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Update Credentials
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">
              No connector credentials configured yet. Add credentials to enable
              integration with your systems.
            </p>
          </div>
        )}

        {/* SFTP Credentials Section - Only shown for primary cloudified connector */}
        {sftpCredentials && (
          <div className="mt-8 border-t pt-6">
            <div className="flex items-center mb-4">
              <Server className="h-5 w-5 text-blue-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                SFTP Account Details
              </h3>
            </div>
            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <p className="text-sm text-blue-700">
                These are your SFTP credentials for the {primaryConnectorName}{" "}
                connector. Use these details to connect to the SFTP server for
                secure file transfers.
              </p>
            </div>
            <div className="space-y-4 bg-gray-50 p-4 rounded-md">
              <div>
                <h3 className="text-sm font-medium text-gray-500">
                  Server URL
                </h3>
                <p className="mt-1 text-sm text-gray-900">
                  {sftpCredentials.url}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Port</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {sftpCredentials.port}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Username</h3>
                <p className="mt-1 text-sm text-gray-900">
                  {sftpCredentials.username}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Password</h3>
                <div className="mt-1 flex items-center">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={sftpCredentials.password}
                    readOnly
                    className="text-sm text-gray-900 bg-transparent border-none p-0 focus:ring-0"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="ml-2 text-gray-500 hover:text-gray-700"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New Credential Modal */}
      <ConnectorCredentialsModal
        isOpen={showNewCredentialModal}
        onClose={() => setShowNewCredentialModal(false)}
        title="Add New Connector"
        connectors={availableConnectors}
        selectedConnector={selectedConnector}
        credentials={credentials}
        error={error}
        loading={loading}
        showConnectorSelect={true}
        onConnectorSelect={handleConnectorSelect}
        onCredentialChange={handleCredentialChange}
        onSubmit={handleSubmitNewCredential}
        onCancel={() => setShowNewCredentialModal(false)}
      />

      {/* Update Credential Modal */}
      <ConnectorCredentialsModal
        isOpen={showUpdateCredentialModal}
        onClose={() => setShowUpdateCredentialModal(false)}
        title="Update Connector Credentials"
        connectors={[]}
        selectedConnector={selectedConnector}
        credentials={credentials}
        error={error}
        loading={loading}
        showConnectorSelect={false}
        onConnectorSelect={() => {}}
        onCredentialChange={handleCredentialChange}
        onSubmit={handleSubmitUpdateCredential}
        onCancel={() => setShowUpdateCredentialModal(false)}
      />
    </div>
  );
}
